import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import MainLayout from '../layouts/MainLayout.vue';

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../pages/login/LoginPage.vue'),
    meta: { title: '登录 - 工程北米有机农产品溯源系统', requiresAuth: false },
  },
  {
    path: '/',
    component: MainLayout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../pages/dashboard/DashboardPage.vue'),
        meta: { title: '数据大屏概览', icon: 'DataLine' },
      },
      // 1. 地块管理
      {
        path: 'land/bases',
        name: 'LandBases',
        component: () => import('../pages/land/BasesPage.vue'),
        meta: { title: '种植基地管理', parent: 'land', icon: 'OfficeBuilding' },
      },
      {
        path: 'land/list',
        name: 'LandList',
        component: () => import('../pages/land/LandListPage.vue'),
        meta: { title: '地块档案列表', parent: 'land', icon: 'Location' },
      },
      {
        path: 'land/varieties',
        name: 'LandVarieties',
        component: () => import('../pages/land/VarietiesPage.vue'),
        meta: { title: '水稻品种管理', parent: 'land', icon: 'Goods' },
      },
      {
        path: 'land/map',
        name: 'LandMap',
        component: () => import('../pages/land/LandMapPage.vue'),
        meta: { title: '基地电子地图', parent: 'land', icon: 'MapLocation' },
      },
      {
        path: 'land/certificates',
        name: 'LandCertificates',
        component: () => import('../pages/land/CertificatesPage.vue'),
        meta: { title: '有机资质归档', parent: 'land', icon: 'DocumentChecked' },
      },
      {
        path: 'land/ledgers',
        name: 'OrganicLedgers',
        component: () => import('../pages/land/OrganicLedgerPage.vue'),
        meta: { title: '有机台账归档', parent: 'land', icon: 'FolderChecked' },
      },
      // 2. 种植农事追溯 (农事操作记录、田间巡查日志、每亩用药明细、水稻种植记录、农场视频监控)
      {
        path: 'farming/records',
        name: 'FarmingRecords',
        component: () => import('../pages/farming/FarmingRecordsPage.vue'),
        meta: { title: '农事操作记录', parent: 'farming', icon: 'Calendar' },
      },
      {
        path: 'farming/inspections',
        name: 'FieldInspections',
        component: () => import('../pages/farming/FieldInspectionPage.vue'),
        meta: { title: '田间巡查日志', parent: 'farming', icon: 'DocumentChecked' },
      },
      {
        path: 'farming/pesticides',
        name: 'Pesticides',
        component: () => import('../pages/farming/PesticidesPage.vue'),
        meta: { title: '每亩用药明细', parent: 'farming', icon: 'FirstAidKit' },
      },
      {
        path: 'farming/harvests',
        name: 'HarvestStats',
        component: () => import('../pages/farming/HarvestStatsPage.vue'),
        meta: { title: '水稻种植记录', parent: 'farming', icon: 'PieChart' },
      },
      {
        path: 'farming/videos',
        name: 'VideoMonitor',
        component: () => import('../pages/farming/VideoMonitorPage.vue'),
        meta: { title: '农场视频监控', parent: 'farming', icon: 'VideoCamera' },
      },
      // 3. 生产加工追溯 (生产订单；MES 任务与质检报告随订单同步，在订单全链路追溯中查看)
      {
        path: 'processing/orders',
        name: 'Orders',
        component: () => import('../pages/processing/OrdersPage.vue'),
        meta: { title: '生产订单管理', parent: 'processing', icon: 'Document' },
      },
      // 4. 溯源码管理 (溯源和核验统计、企业赋码管理、品类赋码管理、一物一码批量生成、二维码印刷导出)
      {
        path: 'tracecode/stats',
        name: 'TraceStats',
        component: () => import('../pages/tracecode/TraceStatsPage.vue'),
        meta: { title: '溯源和核验统计', parent: 'tracecode', icon: 'TrendCharts' },
      },
      {
        path: 'tracecode/enterprises',
        name: 'TraceEnterprises',
        component: () => import('../pages/processing/EnterprisesPage.vue'),
        meta: { title: '企业赋码管理', parent: 'tracecode', icon: 'OfficeBuilding' },
      },
      {
        path: 'tracecode/products',
        name: 'TraceProducts',
        component: () => import('../pages/processing/ProductsPage.vue'),
        meta: { title: '品类赋码管理', parent: 'tracecode', icon: 'Goods' },
      },
      {
        path: 'tracecode/generate',
        name: 'CodeGenerate',
        component: () => import('../pages/tracecode/CodeGeneratePage.vue'),
        meta: { title: '一物一码批量生成', parent: 'tracecode', icon: 'Lightning' },
      },
      {
        path: 'tracecode/packages',
        name: 'CodePackages',
        component: () => import('../pages/tracecode/CodePackagesPage.vue'),
        meta: { title: '二维码印刷导出', parent: 'tracecode', icon: 'Download' },
      },
      // 5. 系统与权限 (用户管理、角色管理、日志审计)
      {
        path: 'system/users',
        name: 'SystemUsers',
        component: () => import('../pages/system/UsersPage.vue'),
        meta: { title: '用户管理', parent: 'system', icon: 'User' },
      },
      {
        path: 'system/roles',
        name: 'SystemRoles',
        component: () => import('../pages/system/RolesPage.vue'),
        meta: { title: '角色管理', parent: 'system', icon: 'SetUp' },
      },
      {
        path: 'system/logs',
        name: 'SystemLogs',
        component: () => import('../pages/system/LogsPage.vue'),
        meta: { title: '日志审计', parent: 'system', icon: 'Document' },
      },
      {
        path: 'system/notarizations',
        name: 'SystemNotarizations',
        component: () => import('../pages/system/NotarizationFilesPage.vue'),
        meta: { title: '公证文件管理', parent: 'system', icon: 'Stamp' },
      },
      {
        path: 'system/brand-channels',
        name: 'SystemBrandChannels',
        component: () => import('../pages/system/BrandChannelsPage.vue'),
        meta: { title: '关于我们配置', parent: 'system', icon: 'Shop' },
      },
    ],
  },
  {
    path: '/403',
    name: 'Exception403',
    component: () => import('../pages/exception/403.vue'),
    meta: { title: '403 暂无权限' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../pages/exception/404.vue'),
    meta: { title: '404 页面未找到' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

import { useAuthStore } from '../store/useAuthStore';
import { AUTH_STORAGE_KEYS } from '@trace/shared/constants';

// Navigation Guard (路由鉴权与未登录拦截)
router.beforeEach((to, _, next) => {
  if (to.meta?.title) {
    document.title = `${to.meta.title} - 工程北米有机农产品溯源系统`;
  }

  // 检查当前登录状态
  let isAuth = false;
  try {
    const authStore = useAuthStore();
    isAuth = authStore.isAuthenticated && !!authStore.token;
  } catch {}

  if (!isAuth) {
    const saved = sessionStorage.getItem(AUTH_STORAGE_KEYS.token) || localStorage.getItem(AUTH_STORAGE_KEYS.token);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.token) {
          isAuth = true;
        }
      } catch {}
    }
  }

  // 1. 若访问登录页
  if (to.path === '/login') {
    if (isAuth) {
      // 已登录直接跳转工作台
      next({ path: '/dashboard', replace: true });
    } else {
      next();
    }
    return;
  }

  // 2. 若访问受保护页面且未登录，重定向至 /login
  if (!isAuth) {
    next({
      path: '/login',
      query: { redirect: to.fullPath !== '/' && to.fullPath !== '/dashboard' ? to.fullPath : undefined },
      replace: true,
    });
    return;
  }

  // 3. 路由级 RBAC 权限校验 — 根据路由 meta.parent 检查用户是否有访问权限
  try {
    const authStore = useAuthStore();
    const routeParent = to.meta?.parent as string | undefined;

    // 无需权限的页面（dashboard、异常页等）直接放行
    if (!routeParent) {
      next();
      return;
    }

    // 超级管理员或拥有通配符权限的用户直接放行
    if (authStore.permissions.includes('*')) {
      next();
      return;
    }

    // 检查用户是否拥有该模块下的任意权限
    const hasModuleAccess = authStore.permissions.some(
      (p) => p === routeParent || p.startsWith(`${routeParent}:`)
    );

    if (!hasModuleAccess) {
      next({ path: '/403', replace: true });
      return;
    }
  } catch {
    // Store not ready, allow access
  }

  next();
});

export default router;
